import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Modal from "../../../../../../../../../components/Modal/Modal";
import SearchableDropdown from "../../../../../../../../../components/SearchableDropdown/SearchableDropdown";
import Button from "../../../../../../../../../components/Button/Button";
import { RootState } from "../../../../../../../../../redux/store";
import { getHelpdeskLabels } from "../../../../../../../../../utils/constants/mappings";
import { FEATURE_REQUESTS_SYSTEM_NAME } from "../../../../CreateTicket/utils/constants";
import {
  getEffectiveDropdownOptions,
  type HelpdeskCustomFieldDefinition,
} from "../utils/helpdeskCustomFields";
import { getSystemIcon } from "../utils/systemIcons";

const DROPDOWN_LABEL_CLASS =
  "text-md font-medium text-verba-field-label dark:text-verba-field-label-dk";

const dropdownOptions = (values: string[], withIcon = false) =>
  values?.map((value) => {
    const content = (
      <span className="flex items-center gap-2 min-w-0 text-md font-medium text-verba-indigo dark:text-verba-indigo-dk">
        {withIcon && <span className="shrink-0">{getSystemIcon(value)}</span>}
        <span className="truncate">{value}</span>
      </span>
    );
    return { value, label: value, content, selectedOptionContent: content };
  });

export interface TicketCategoryChangeModalProps {
  open: boolean;
  categoryDisplayName: string;
  /** Categories like Sales / Marketing derive their system name — then only the issue type is asked. */
  forcedSystemName?: string | null;
  systemNameField: HelpdeskCustomFieldDefinition | null;
  issueTypeField: HelpdeskCustomFieldDefinition | null;
  onSubmit: (values: { systemName: string; issueType: string }) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const TicketCategoryChangeModal = ({
  open,
  categoryDisplayName,
  forcedSystemName = null,
  systemNameField,
  issueTypeField,
  onSubmit,
  onCancel,
  isLoading = false,
}: TicketCategoryChangeModalProps) => {
  const region = useSelector((state: RootState) => state?.auth?.client?.region);
  const helpdeskLabels = getHelpdeskLabels(region ?? "UK");

  const [systemName, setSystemName] = useState("");
  const [issueType, setIssueType] = useState("");

  useEffect(() => {
    if (open) {
      setSystemName(forcedSystemName ?? "");
      setIssueType("");
    }
  }, [open, forcedSystemName]);

  const systemOptions = useMemo(
    () => dropdownOptions(systemNameField?.values_for_dropdown ?? [], true),
    [systemNameField]
  );

  const issueOptions = useMemo(() => {
    if (!issueTypeField || !systemName) return [];
    return dropdownOptions(
      getEffectiveDropdownOptions(issueTypeField, { "system-name": systemName })
    );
  }, [issueTypeField, systemName]);

  const isDirectIssueMode = Boolean(forcedSystemName);
  const isFeatureRequest = systemName === FEATURE_REQUESTS_SYSTEM_NAME;
  const hasIssueOptions = issueOptions?.length > 0;
  const isIssueTypeDisabled = (!isDirectIssueMode && !systemName) || isLoading;

  const issueTypePlaceholder =
    !isDirectIssueMode && !systemName
      ? "Please select a system name first"
      : !hasIssueOptions
        ? `No issue types available for "${systemName}"`
        : "Select issue type";

  const canSubmit =
    Boolean(systemName) &&
    (isFeatureRequest || !hasIssueOptions || Boolean(issueType));

  const handleSystemChange = (value: string) => {
    setSystemName(value);
    setIssueType("");
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({ systemName, issueType: isFeatureRequest ? "" : issueType });
  };

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen && !isLoading) onCancel();
      }}
      title={`Move to ${categoryDisplayName}`}
      titleClassName="!text-lg !font-medium text-heading-text dark:text-heading-text-dk"
      headerClassName="px-6 pt-6"
      contentClassName="!overflow-visible w-[420px] max-w-[90vw] bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk"
      overlayClassName="bg-black/80"
      preventOutsideClick={isLoading}
      closeOnEscape={!isLoading}
    >
      <div className="flex flex-col gap-9">
        <div className="flex flex-col gap-3">
          <p className="text-md font-normal text-data dark:text-data-dk">
            Select {isDirectIssueMode ? "an issue type" : "a system name and issue type"}{" "}
            to move this {helpdeskLabels.ticketSingularLower} to{" "}
            <span className="font-semibold">{categoryDisplayName}</span>.
          </p>

          <div className="flex flex-col gap-3 w-[264px]">
            {!isDirectIssueMode && (
              <div className="flex flex-col gap-1.5">
                <label className={DROPDOWN_LABEL_CLASS}>System Name</label>
                <SearchableDropdown
                  id="category-change-system-name-dropdown"
                  options={systemOptions}
                  value={systemName || null}
                  onChange={handleSystemChange}
                  disabled={isLoading}
                  placeholder="Select system"
                  className="w-[264px]"
                  triggerClassName="!rounded-lg !h-[36px] !border-border-gray dark:!border-border-gray-dk"
                  selectedOptionContainerClassName="!px-2 !py-0 h-full"
                  dropdownClassName="!bg-popper-bg-fixed-white dark:!bg-popper-bg-fixed-white-dk !py-0"
                  dropdownMenuClassName="!max-h-[150px]"
                  optionsListClassName="!py-0"
                  placeholderClassName="!text-md !text-gray-placeholder dark:!text-gray-placeholder-dk"
                  searchPlaceholder="Search system names..."
                  sectionHeading="System Name"
                  searchInputClassName="!rounded-t-[8px]"
                  ariaLabel="System Name"
                />
              </div>
            )}

            {!isFeatureRequest && (
              <div className="flex flex-col gap-1.5">
                <label className={DROPDOWN_LABEL_CLASS}>Issue Type</label>
                <SearchableDropdown
                  id="category-change-issue-type-dropdown"
                  options={issueOptions}
                  value={issueType || null}
                  onChange={(value) => setIssueType(value)}
                  disabled={isIssueTypeDisabled}
                  placeholder={issueTypePlaceholder}
                  className="w-[264px]"
                  triggerClassName="!rounded-lg !h-[36px] !border-border-gray dark:!border-border-gray-dk"
                  selectedOptionContainerClassName="!px-2 !py-0 h-full"
                  dropdownClassName="!bg-popper-bg-fixed-white dark:!bg-popper-bg-fixed-white-dk !py-0"
                  dropdownMenuClassName="!max-h-[150px]"
                  optionClassName="!py-[8px]"
                  optionsListClassName="!py-0"
                  placeholderClassName="!text-md !text-gray-placeholder dark:!text-gray-placeholder-dk"
                  searchPlaceholder="Search issue types..."
                  sectionHeading="Issue Type"
                  searchInputClassName="!rounded-t-[8px]"
                  ariaLabel="Issue Type"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            innerText="Cancel"
            onClick={onCancel}
            disabled={isLoading}
            buttonContainerClass="min-w-[100px] min-h-[26px] py-[2px] px-2 text-md border border-border-gray dark:border-border-gray-dk bg-transparent dark:bg-transparent rounded-md cursor-pointer"
            innerTextClass="text-heading-text dark:text-heading-text-dk text-md font-semibold"
          />
          <Button
            innerText={`Update ${helpdeskLabels.ticketCategory}`}
            onClick={handleSubmit}
            disabled={isLoading || !canSubmit}
            isLoading={isLoading}
            buttonContainerClass="disabled:opacity-30 min-w-[140px] min-h-[26px] py-[2px] px-4 text-md border border-verba-indigo dark:border-verba-indigo-dk bg-verba-indigo dark:bg-verba-indigo-dk rounded-md cursor-pointer"
            innerTextClass="text-pure-white dark:text-pure-white-dk text-md font-semibold"
            loaderColor="text-pure-white dark:text-pure-white-dk"
            loaderSize={15}
          />
        </div>
      </div>
    </Modal>
  );
};

export default TicketCategoryChangeModal;
